import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, Inbox, TrendingUp, CalendarDays, ArrowLeft, Loader2, Users, UserCheck, Eye } from "lucide-react";
import axios from "axios";
import LogoMark from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
    const [token, setToken] = useState(() => sessionStorage.getItem("wr_admin_token") || "");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [messages, setMessages] = useState([]);
    const [visits, setVisits] = useState([]);

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const load = useCallback(async () => {
        try {
            const [s, m, v] = await Promise.all([
                axios.get(`${API}/admin/stats`, auth),
                axios.get(`${API}/admin/messages`, auth),
                axios.get(`${API}/admin/visits`, auth),
            ]);
            setStats(s.data);
            setMessages(m.data);
            setVisits(v.data);
            setError("");
        } catch {
            setToken("");
            sessionStorage.removeItem("wr_admin_token");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (token) load();
    }, [token, load]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/admin/login`, { password });
            sessionStorage.setItem("wr_admin_token", data.token);
            setToken(data.token);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "Prihlásenie zlyhalo.");
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        sessionStorage.removeItem("wr_admin_token");
        setToken("");
        setStats(null);
        setMessages([]);
    };

    const fmt = (iso) => {
        const d = new Date(iso);
        return d.toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    return (
        <div className="min-h-screen bg-ink text-ivory font-sans">
            <header className="border-b border-white/5 px-5 sm:px-8 py-4">
                <div className="mx-auto flex max-w-5xl items-center justify-between">
                    <div className="flex items-center gap-3">
                        <LogoMark size={26} />
                        <span className="font-display text-lg font-medium">
                            Webryno <span className="text-glacier">Admin</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <a
                            data-testid="admin-back-link"
                            href="/"
                            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-fog transition-colors hover:text-ivory"
                        >
                            <ArrowLeft size={13} /> Späť na web
                        </a>
                        {token && (
                            <button
                                data-testid="admin-logout-button"
                                onClick={logout}
                                className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-fog transition-colors hover:border-glacier hover:text-glacier"
                            >
                                <LogOut size={13} /> Odhlásiť
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-5 sm:px-8 py-14">
                {!token ? (
                    <motion.form
                        data-testid="admin-login-form"
                        onSubmit={login}
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mx-auto mt-16 max-w-sm rounded-lg border border-white/10 bg-coal p-8"
                    >
                        <div className="mb-6 inline-flex rounded-sm border border-glacier/25 p-3 text-glacier">
                            <Lock size={20} strokeWidth={1.6} />
                        </div>
                        <h1 className="font-display text-2xl font-medium">Admin prihlásenie</h1>
                        <p className="mt-2 text-sm text-fog">Prehľad dopytov z kontaktného formulára.</p>
                        <input
                            data-testid="admin-password-input"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Admin heslo"
                            className="mt-6 w-full rounded-sm border border-white/10 bg-ink px-5 py-3.5 text-sm text-ivory placeholder:text-fog/60 outline-none transition-colors focus:border-glacier"
                        />
                        {error && (
                            <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">
                                {error}
                            </p>
                        )}
                        <button
                            data-testid="admin-login-button"
                            type="submit"
                            disabled={loading}
                            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-glacier px-6 py-3.5 font-display text-sm font-semibold uppercase tracking-widest text-ink transition-colors hover:bg-ivory disabled:opacity-60"
                        >
                            {loading && <Loader2 size={15} className="animate-spin" />}
                            Prihlásiť
                        </button>
                    </motion.form>
                ) : (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <h1 className="font-display text-3xl font-medium">Dopyty od klientov</h1>
                        <p className="mt-2 text-sm text-fog">Prehľad všetkých správ odoslaných cez kontaktný formulár.</p>

                        <div data-testid="admin-stats" className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {[
                                { icon: Inbox, label: "Celkom dopytov", value: stats?.total ?? "—" },
                                { icon: TrendingUp, label: "Posledných 7 dní", value: stats?.last_7_days ?? "—" },
                                { icon: CalendarDays, label: "Posledných 30 dní", value: stats?.last_30_days ?? "—" },
                                { icon: Users, label: "Návštevníci celkom", value: stats?.visitors_total ?? "—" },
                                { icon: UserCheck, label: "Návštevníci 7 dní", value: stats?.visitors_7d ?? "—" },
                                { icon: Eye, label: "Zobrazení stránky", value: stats?.views_total ?? "—" },
                            ].map((s, i) => (
                                <div key={i} data-testid={`admin-stat-${i}`} className="rounded-lg border border-white/10 bg-coal p-6">
                                    <s.icon size={18} className="text-glacier" />
                                    <div className="mt-4 font-display text-3xl font-medium">{s.value}</div>
                                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        <h2 className="mt-14 font-display text-xl font-medium">Návštevnosť · posledných 30 dní</h2>
                        <div data-testid="admin-visits-chart" className="mt-5 rounded-lg border border-white/10 bg-coal p-6">
                            <div className="flex h-36 items-end gap-1">
                                {(stats?.daily ?? []).map((d) => {
                                    const max = Math.max(1, ...(stats?.daily ?? []).map((x) => x.count));
                                    const h = d.count === 0 ? 3 : Math.max(8, (d.count / max) * 100);
                                    return (
                                        <div
                                            key={d.date}
                                            title={`${d.date}: ${d.count}`}
                                            className="flex-1 rounded-sm bg-glacier/80 transition-colors hover:bg-glacier"
                                            style={{ height: `${h}%` }}
                                        />
                                    );
                                })}
                            </div>
                            <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.15em] text-fog">
                                <span>{stats?.daily?.[0]?.date ?? ""}</span>
                                <span>{stats?.daily?.[29]?.date ?? ""}</span>
                            </div>
                        </div>

                        <h2 className="mt-14 font-display text-xl font-medium">Posledné návštevy</h2>
                        <div data-testid="admin-visits-list" className="mt-5 overflow-hidden rounded-lg border border-white/10">
                            {visits.length === 0 && (
                                <p className="bg-coal p-6 text-center text-sm text-fog">Zatiaľ žiadne návštevy.</p>
                            )}
                            {visits.map((v, i) => (
                                <div
                                    key={i}
                                    data-testid={`admin-visit-${i}`}
                                    className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 bg-coal px-5 py-3.5 last:border-b-0"
                                >
                                    <span className="font-mono text-[11px] text-fog">{fmt(v.ts)}</span>
                                    <span className="font-mono text-[11px] text-ivory/80">{v.path}</span>
                                    <span className={`font-mono text-[11px] ${v.ref ? "text-glacier" : "text-fog/60"}`}>
                                        {v.ref ? `zdroj: ${v.ref}` : "priama návšteva"}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <h2 className="mt-14 font-display text-xl font-medium">Dopyty</h2>
                        <div data-testid="admin-messages-list" className="mt-5 space-y-4">
                            {messages.length === 0 && (
                                <p className="rounded-lg border border-white/10 bg-coal p-8 text-center text-sm text-fog">
                                    Zatiaľ žiadne dopyty.
                                </p>
                            )}
                            {messages.map((m) => (
                                <article key={m.id} data-testid={`admin-message-${m.id}`} className="rounded-lg border border-white/10 bg-coal p-6">
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                        <h3 className="font-display text-lg font-medium text-ivory">{m.name}</h3>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">{fmt(m.created_at)}</span>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                                        <a href={`mailto:${m.email}`} className="text-glacier hover:underline">{m.email}</a>
                                        {m.phone && (
                                            <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="text-glacier hover:underline">{m.phone}</a>
                                        )}
                                    </div>
                                    <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-fog">{m.message}</p>
                                </article>
                            ))}
                        </div>
                    </motion.div>
                )}
            </main>
        </div>
    );
}
